import { IStyleParam, StyleParamType, ISettingsColor, ISettingsFont } from '@wix/tpa-settings';
import { ButtonStyle } from '../../../settingsParams';
import stylesParams from '../../../stylesParams';

const HOLLOW_STYLES = [ButtonStyle.EMPTY_CORNERED, ButtonStyle.EMPTY_ROUNDED];
const RECTANGLE_STYLES = [ButtonStyle.EMPTY_CORNERED, ButtonStyle.FULL_CORNERED];

export function isRectangularStyle(style: ButtonStyle) {
  return RECTANGLE_STYLES.includes(style);
}

export function isHollowStyle(style: ButtonStyle) {
  return HOLLOW_STYLES.includes(style);
}

interface RegularButton {
  font: IStyleParam<StyleParamType.Font, ISettingsFont>;
  fontColor: IStyleParam<StyleParamType.Color, ISettingsColor>;
  borderWidth: IStyleParam<StyleParamType.Number, number>;
  alignment: IStyleParam<StyleParamType.Number, number>;
  width: IStyleParam<StyleParamType.Number, number>;
  borderColor: IStyleParam<StyleParamType.Color, ISettingsColor>;
  backgroundColor: IStyleParam<StyleParamType.Color, ISettingsColor>;
  borderRadius: IStyleParam<StyleParamType.Number, number>;
}

interface ButtonHover {
  hoverFontColor: IStyleParam<StyleParamType.Color, ISettingsColor>;
  hoverBorderColor: IStyleParam<StyleParamType.Color, ISettingsColor>;
  hoverBackgroundColor: IStyleParam<StyleParamType.Color, ISettingsColor>;
}

export function getRegularButtonHoverProperties(style: ButtonStyle): ButtonHover {
  const isHollow = isHollowStyle(style);
  const isRectangle = isRectangularStyle(style);

  if (isHollow) {
    if (isRectangle) {
      return {
        hoverFontColor: stylesParams.buttonEmptyCorneredFontColorHovered,
        hoverBackgroundColor: stylesParams.buttonEmptyCorneredBackgroundHovered,
        hoverBorderColor: stylesParams.buttonEmptyCorneredBorderOpacityAndColorHovered,
      };
    } else {
      return {
        hoverFontColor: stylesParams.buttonEmptyRoundedFontColorHovered,
        hoverBackgroundColor: stylesParams.buttonEmptyRoundedBackgroundHovered,
        hoverBorderColor: stylesParams.buttonEmptyRoundedBorderOpacityAndColorHovered,
      };
    }
  } else {
    if (isRectangle) {
      return {
        hoverFontColor: stylesParams.buttonFullCorneredFontColorHovered,
        hoverBackgroundColor: stylesParams.buttonFullCorneredBackgroundHovered,
        hoverBorderColor: stylesParams.buttonFullCorneredBorderOpacityAndColorHovered,
      };
    } else {
      return {
        hoverFontColor: stylesParams.buttonFullRoundedFontColorHovered,
        hoverBackgroundColor: stylesParams.buttonFullRoundedBackgroundHovered,
        hoverBorderColor: stylesParams.buttonFullRoundedBorderOpacityAndColorHovered,
      };
    }
  }
}

export function getRegularButtonProperties(style: ButtonStyle): RegularButton {
  const isHollow = isHollowStyle(style);
  const isRectangle = isRectangularStyle(style);

  if (isHollow) {
    if (isRectangle) {
      return {
        font: stylesParams.buttonEmptyCorneredFontStyle,
        fontColor: stylesParams.buttonEmptyCorneredFontColor,
        backgroundColor: stylesParams.buttonEmptyCorneredBackground,
        borderColor: stylesParams.buttonEmptyCorneredBorderOpacityAndColor,
        alignment: stylesParams.buttonEmptyCorneredAlignment,
        width: stylesParams.buttonEmptyCorneredWidth,
        borderRadius: stylesParams.buttonEmptyCorneredCornerRadius,
        borderWidth: stylesParams.buttonEmptyCorneredBorderWidth,
      };
    } else {
      return {
        font: stylesParams.buttonEmptyRoundedFontStyle,
        fontColor: stylesParams.buttonEmptyRoundedFontColor,
        backgroundColor: stylesParams.buttonEmptyRoundedBackground,
        borderColor: stylesParams.buttonEmptyRoundedBorderOpacityAndColor,
        alignment: stylesParams.buttonEmptyRoundedAlignment,
        width: stylesParams.buttonEmptyRoundedWidth,
        borderRadius: stylesParams.buttonEmptyRoundedCornerRadius,
        borderWidth: stylesParams.buttonEmptyRoundedBorderWidth,
      };
    }
  } else {
    if (isRectangle) {
      return {
        font: stylesParams.buttonFullCorneredFontStyle,
        fontColor: stylesParams.buttonFullCorneredFontColor,
        backgroundColor: stylesParams.buttonFullCorneredBackground,
        borderColor: stylesParams.buttonFullCorneredBorderOpacityAndColor,
        alignment: stylesParams.buttonFullCorneredAlignment,
        width: stylesParams.buttonFullCorneredWidth,
        borderRadius: stylesParams.buttonFullCorneredCornerRadius,
        borderWidth: stylesParams.buttonFullCorneredBorderWidth,
      };
    } else {
      return {
        font: stylesParams.buttonFullRoundedFontStyle,
        fontColor: stylesParams.buttonFullRoundedFontColor,
        backgroundColor: stylesParams.buttonFullRoundedBackground,
        borderColor: stylesParams.buttonFullRoundedBorderOpacityAndColor,
        alignment: stylesParams.buttonFullRoundedAlignment,
        width: stylesParams.buttonFullRoundedWidth,
        borderRadius: stylesParams.buttonFullRoundedCornerRadius,
        borderWidth: stylesParams.buttonFullRoundedBorderWidth,
      };
    }
  }
}

// For highlighted version we omit width and font setting because we
// want to prevent height difference.
type HighlightedButton = Omit<RegularButton, 'font' | 'borderWidth' | 'width' | 'alignment'>;

export function getHighlightedButtonProperties(style: ButtonStyle): HighlightedButton {
  const isHollow = isHollowStyle(style);
  const isRectangle = isRectangularStyle(style);

  if (isHollow) {
    if (isRectangle) {
      return {
        fontColor: stylesParams.buttonEmptyCorneredFontColorHighlighted,
        borderColor: stylesParams.buttonEmptyCorneredBorderOpacityAndColorHighlighted,
        borderRadius: stylesParams.buttonEmptyCorneredCornerRadiusHighlighted,
        backgroundColor: stylesParams.buttonEmptyCorneredBackgroundHighlighted,
      };
    } else {
      return {
        fontColor: stylesParams.buttonEmptyRoundedFontColorHighlighted,
        borderColor: stylesParams.buttonEmptyRoundedBorderOpacityAndColorHighlighted,
        borderRadius: stylesParams.buttonEmptyRoundedCornerRadiusHighlighted,
        backgroundColor: stylesParams.buttonEmptyRoundedBackgroundHighlighted,
      };
    }
  } else {
    if (isRectangle) {
      return {
        fontColor: stylesParams.buttonFullCorneredFontColorHighlighted,
        borderColor: stylesParams.buttonFullCorneredBorderOpacityAndColorHighlighted,
        borderRadius: stylesParams.buttonFullCorneredCornerRadiusHighlighted,
        backgroundColor: stylesParams.buttonFullCorneredBackgroundHighlighted,
      };
    } else {
      return {
        fontColor: stylesParams.buttonFullRoundedFontColorHighlighted,
        borderColor: stylesParams.buttonFullRoundedBorderOpacityAndColorHighlighted,
        borderRadius: stylesParams.buttonFullRoundedCornerRadiusHighlighted,
        backgroundColor: stylesParams.buttonFullRoundedBackgroundHighlighted,
      };
    }
  }
}

export function getHighlightedButtonHoverProperties(style: ButtonStyle): ButtonHover {
  const isHollow = isHollowStyle(style);
  const isRectangle = isRectangularStyle(style);

  if (isHollow) {
    if (isRectangle) {
      return {
        hoverFontColor: stylesParams.buttonEmptyCorneredFontColorHighlightedHovered,
        hoverBorderColor: stylesParams.buttonEmptyCorneredBorderOpacityAndColorHighlightedHovered,
        hoverBackgroundColor: stylesParams.buttonEmptyCorneredBackgroundHighlightedHovered,
      };
    } else {
      return {
        hoverFontColor: stylesParams.buttonEmptyRoundedFontColorHighlightedHovered,
        hoverBorderColor: stylesParams.buttonEmptyRoundedBorderOpacityAndColorHighlightedHovered,
        hoverBackgroundColor: stylesParams.buttonEmptyRoundedBackgroundHighlightedHovered,
      };
    }
  } else {
    if (isRectangle) {
      return {
        hoverFontColor: stylesParams.buttonFullCorneredFontColorHighlightedHovered,
        hoverBorderColor: stylesParams.buttonFullCorneredBorderOpacityAndColorHighlightedHovered,
        hoverBackgroundColor: stylesParams.buttonFullCorneredBackgroundHighlightedHovered,
      };
    } else {
      return {
        hoverFontColor: stylesParams.buttonFullRoundedFontColorHighlightedHovered,
        hoverBorderColor: stylesParams.buttonFullRoundedBorderOpacityAndColorHighlightedHovered,
        hoverBackgroundColor: stylesParams.buttonFullRoundedBackgroundHighlightedHovered,
      };
    }
  }
}
